import { useDispatch } from 'react-redux'
import { useState, useEffect, useRef } from 'react'
import { length, remove } from 'ramda'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { colors } from '@/styles'
import { createModal, useModalize } from '@/components/modalize/mui'
import { DescribeBodyType, GenerateTextProps, CustomWindow } from '../types'
import GeneratedTextArea from '../elements/textarea'
import ModalTitle from '../elements/modal-title'
import { convertToReadableStream } from '../utils'

const Wrapper = styled.div``

export const SectionWrapper = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`

const ScrollableSectionWrapper = styled(SectionWrapper)`
  overflow: scroll;
  height: 100%;
`

const DisclaimerText = styled.p`
  font-family: 'Inter';
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.grey48};
`

declare let window: CustomWindow

type GeneratedTextList = string[]

const CreateGeneratedText: React.FC<GenerateTextProps> = ({
  heading,
  listingId,
  formValues = {},
  eventId,
  setDescriptionFormValue
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const abortController = useRef<AbortController | null>(null)
  const { close } = useModalize()
  const initialGenerateRef = useRef(false)
  const [generatedTextList, setGeneratedTextList] = useState<GeneratedTextList>([])
  const [isDone, setIsDone] = useState(false)
  const [generatedText, setGeneratedText] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleDone = () => {
    setIsDone(true)
    setIsLoading(false)
  }

  const handleGeneratedTextUpdate = (val: string) => {
    setGeneratedText(txt => txt + val)
  }

  const getInitialDescription = async (data: DescribeBodyType) => {
    setIsLoading(true)
    const controller = new AbortController()
    abortController.current = controller
    const signal = abortController.current?.signal

    const { response, body, error } = await dispatch.listing.generateDynamicSmartListingDescription(
      {
        data: data,
        signal: signal
      }
    )

    if (error) {
      setIsLoading(false)
      abortController.current = null
      close()
      return
    }

    if (response?.ok) {
      window?.dataLayer?.push({
        event: eventId
      })
    }
    convertToReadableStream(body, handleGeneratedTextUpdate, handleDone)
  }

  const getUpdatedDescription = async (tone: string, content: string = '') => {
    setIsLoading(true)
    const controller = new AbortController()
    abortController.current = controller
    const signal = abortController.current?.signal

    const { body } = await dispatch.listing.generateUpdatedListingSmartDescription({
      tone: tone,
      content: content,
      listingId: listingId,
      signal: signal
    })

    convertToReadableStream(body, handleGeneratedTextUpdate, handleDone)
  }

  const handleUseClick = (value: string) => {
    setDescriptionFormValue(value)
    close()
  }

  const handleRemove = (index: number) => {
    if (index === 0) {
      close()
      return
    }
    const val = remove(index, 1, generatedTextList)
    setGeneratedTextList(val)
  }

  useEffect(() => {
    if (initialGenerateRef.current) return
    initialGenerateRef.current = true

    getInitialDescription(formValues)
  }, [])

  useEffect(() => {
    if (isDone) {
      setGeneratedTextList(x => [...x, generatedText])
      setGeneratedText('')
      abortController.current = null
      setIsDone(false)
    }
  }, [isDone, setIsDone, setGeneratedTextList, setGeneratedText])

  const lengthGeneratedText = length(generatedTextList)

  const handleModalClose = () => {
    if (abortController.current) {
      abortController.current?.abort()
    }
    close()
  }

  return (
    <Wrapper>
      <ModalTitle heading={heading} onClose={handleModalClose} />
      <ScrollableSectionWrapper>
        {generatedTextList?.map((text, index) => {
          const isLastElement = index === lengthGeneratedText - 1
          return (
            <GeneratedTextArea
              key={`generated-text-${index}`}
              active={isLastElement && !isLoading}
              text={text}
              getUpdatedDescription={getUpdatedDescription}
              handleUseClick={handleUseClick}
              handleRemove={() => handleRemove(index)}
            />
          )
        })}
        {isLoading && (
          <GeneratedTextArea
            active
            isLoading={isLoading}
            text={generatedText}
            getUpdatedDescription={getUpdatedDescription}
            handleUseClick={handleUseClick}
          />
        )}
        <DisclaimerText>
          {t(
            'l.smart_description.generate_text.disclaimer.text',
            `By using this generated text, you acknowledge that you have reviewed the description for
          accuracy. AI models are still in their infancy and can make errors despite their best
          efforts.`
          )}
        </DisclaimerText>
      </ScrollableSectionWrapper>
    </Wrapper>
  )
}

export default CreateGeneratedText

export const show = createModal(CreateGeneratedText, {
  width: '900px',
  maxHeight: '750px',
  clickOutside: false
})
